import React, { useState } from 'react';
import { SITE_CONTENT } from '../constants';
import MediaModal from './MediaModal';

interface RepertoireProps {
  onPageChange: (pageId: string, mediaTitle?: string) => void;
}

const Repertoire: React.FC<RepertoireProps> = ({ onPageChange }) => {
  const { repertorio, media } = SITE_CONTENT;
  const [src, setSrc] = useState(repertorio.image);
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    title: string;
    url: string;
    type: 'audio' | 'video' | 'youtube';
    youtubeId?: string;
  }>({
    isOpen: false,
    title: '',
    url: '',
    type: 'audio'
  });

  const fallback = "https://images.unsplash.com/photo-1514320298574-255c5df6b0a1?q=80&w=2070&auto=format&fit=crop";

  const normalize = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');

  const handlePieceClick = (piece: string, type: 'audio' | 'video') => {
    const audio = media.audios.find(a => normalize(a.title) === normalize(piece));
    const video = media.videos.find(v => normalize(v.title) === normalize(piece));

    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (type === 'video' && video) {
      setModalData({
        isOpen: true,
        title: video.title,
        url: video.url || '',
        type: video.type as 'video' | 'youtube',
        youtubeId: video.id
      });
    } else if (type === 'audio' && audio) {
      setModalData({
        isOpen: true,
        title: audio.title,
        url: audio.url,
        type: 'audio'
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6">
      <MediaModal 
        isOpen={modalData.isOpen}
        onClose={() => setModalData(prev => ({ ...prev, isOpen: false }))}
        title={modalData.title}
        url={modalData.url}
        type={modalData.type}
        youtubeId={modalData.youtubeId}
      />
      
      <div className="flex flex-col space-y-12">
        {/* Intestazione e Immagine con Cornice */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="mb-8">
              <span className="text-gold font-sans uppercase tracking-[0.4em] text-xs block mb-4">La nostra musica</span>
              <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight">
                {repertorio.title}
              </h2>
              <div className="w-16 h-px bg-gold mb-8" />
            </div>
            
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              {repertorio.intro}
            </p>
            
            <div className="p-8 bg-gold/5 border-l-2 border-gold mb-12 backdrop-blur-sm">
              <p className="text-gold-light italic font-serif text-lg">
                "L'armonia è l'unione di voci diverse che cantano la stessa bellezza."
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <div className="relative group">
              {/* Cornice decorativa esterna */}
              <div className="absolute -inset-6 border border-gold/20 pointer-events-none z-0" />
              
              {/* Accenti d'angolo architettonici */}
              <div className="absolute -top-6 -left-6 w-12 h-12 border-t-2 border-l-2 border-gold z-20 pointer-events-none" />
              <div className="absolute -bottom-6 -right-6 w-12 h-12 border-b-2 border-r-2 border-gold z-20 pointer-events-none" />
              
              {/* Contenitore Immagine - Saturazione ridotta all'80% */}
              <div className="relative z-10 w-full overflow-hidden shadow-2xl rounded-sm">
                <img 
                  src={src} 
                  alt="Il nostro repertorio" 
                  className="w-full h-auto block saturate-[0.8]"
                  onError={() => src !== fallback && setSrc(fallback)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Griglia dei Generi */}
        <div className="w-full pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {repertorio.genres.map((genre, idx) => (
              <div key={idx} className="border border-white/10 p-8 hover:border-gold/50 transition-all duration-500 bg-white/5 backdrop-blur-md group relative">
                <div className="absolute top-0 left-0 w-1 h-0 bg-gold group-hover:h-full transition-all duration-500" />
                
                <h3 className="text-xl font-serif text-gold mb-6 uppercase tracking-widest border-b border-gold/20 pb-4 group-hover:text-gold-light transition-colors">
                  {genre.name}
                </h3>
                
                <ul className="space-y-4">
                  {genre.pieces.map((piece, pIdx) => {
                    const hasAudio = media.audios.some(a => normalize(a.title) === normalize(piece));
                    const hasVideo = media.videos.some(v => normalize(v.title) === normalize(piece));

                    return (
                      <React.Fragment key={pIdx}>
                        {/* Versione Audio */}
                        {hasAudio && (
                          <li 
                            className="flex items-center text-white/90 group/item cursor-pointer hover:text-gold transition-colors"
                            onClick={() => handlePieceClick(piece, 'audio')}
                          >
                            <span className="mr-3 w-1.5 h-1.5 rounded-full shrink-0 bg-gold scale-125 shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                            <span className="text-sm md:text-base leading-tight flex-grow">{piece}</span>
                            <div className="flex space-x-2 ml-3 opacity-60 group-hover/item:opacity-100 transition-opacity">
                              <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                              </svg>
                            </div>
                          </li>
                        )}
                        
                        {/* Versione Video */}
                        {hasVideo && (
                          <li 
                            className="flex items-center text-white/90 group/item cursor-pointer hover:text-gold transition-colors"
                            onClick={() => handlePieceClick(piece, 'video')}
                          >
                            <span className="mr-3 w-1.5 h-1.5 rounded-full shrink-0 bg-gold scale-125 shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                            <span className="text-sm md:text-base leading-tight flex-grow">{piece}</span>
                            <div className="flex space-x-2 ml-3 opacity-60 group-hover/item:opacity-100 transition-opacity">
                              {/* Icona Cinepresa (Movie Camera) */}
                              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </li>
                        )}

                        {/* Versione solo testo (se non ha né audio né video) */}
                        {!hasAudio && !hasVideo && (
                          <li className="flex items-center text-white/90">
                            <span className="mr-3 w-1.5 h-1.5 rounded-full shrink-0 bg-gold/40" />
                            <span className="text-sm md:text-base leading-tight flex-grow">{piece}</span>
                          </li>
                        )}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Repertoire;